<template>
  <img :src="displayUrl" :crossorigin="crossOriginMedia" v-bind="$attrs" v-on="$listeners">
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { stacRequestOptions } from '../store/utils';
import axios from 'axios';

/**
 * Renders an image that respects the STAC Browser authentication pipeline.
 *
 * - Query-based auth is applied by resolving the URL through `getRequestUrl`.
 * - Header-based auth (e.g. OIDC/Bearer, Basic) can't be sent by a plain
 *   `<img src>`, so the image is fetched via JS and displayed through a
 *   `blob:` object URL instead.
 * - External URLs (outside the catalog scope / not in `allowedDomains`) never
 *   receive any auth and are loaded directly.
 */
export default {
  name: 'AuthenticatedImage',
  inheritAttrs: false,
  props: {
    src: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      objectUrl: null
    };
  },
  computed: {
    ...mapState(['crossOriginMedia', 'requestHeaders']),
    ...mapGetters(['getRequestUrl', 'isExternalUrl']),
    // URL with query-based auth applied (no-op for external URLs).
    resolvedUrl() {
      return this.getRequestUrl(this.src);
    },
    // Header auth can only be applied to in-scope URLs via a JS request.
    needsHeaderAuth() {
      return !this.isExternalUrl(this.resolvedUrl)
        && Boolean(this.requestHeaders)
        && Object.keys(this.requestHeaders).length > 0;
    },
    displayUrl() {
      return this.objectUrl || this.resolvedUrl;
    }
  },
  watch: {
    resolvedUrl: {
      immediate: true,
      handler: 'load'
    },
    // Reload when the credentials change (login/logout).
    requestHeaders: {
      deep: true,
      handler: 'load'
    }
  },
  beforeDestroy() {
    this.revoke();
  },
  methods: {
    async load() {
      this.revoke();
      if (!this.needsHeaderAuth) {
        return;
      }
      const requestedUrl = this.resolvedUrl;
      try {
        const options = stacRequestOptions(this.$store, { href: this.src });
        const response = await axios(Object.assign(options, { responseType: 'blob' }));
        // Guard against a race where the URL changed while fetching.
        if (requestedUrl !== this.resolvedUrl) {
          return;
        }
        this.objectUrl = URL.createObjectURL(response.data);
      } catch (error) {
        // Fall back to the plain URL (which may render as a broken image).
        this.objectUrl = null;
      }
    },
    revoke() {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = null;
      }
    }
  }
};
</script>
